/**
 * TTS playback sink. Serializes WAV buffers through a single {@link AudioContext}
 * so overlapping `tts_audio` frames play back in order, one at a time. Extracted
 * out of LiveScreen so the live session engine (and its tests) can depend on a
 * small, mockable interface instead of the DOM AudioContext directly.
 */
export interface AudioSink {
  /** Decode + play one WAV buffer. Resolves when playback finishes. */
  enqueue(buf: ArrayBuffer): Promise<void>;
  /** Tear down the context and drop the queue. */
  stop(): void;
}

export class TtsPlayer implements AudioSink {
  private ctx: AudioContext | null = null;
  private queue: Promise<void> = Promise.resolve();

  enqueue(buf: ArrayBuffer): Promise<void> {
    const ctx = this.ctx ?? new AudioContext();
    this.ctx = ctx;
    const job = this.queue.then(async () => {
      try {
        const decoded = await ctx.decodeAudioData(buf.slice(0));
        await new Promise<void>((resolve) => {
          const src = ctx.createBufferSource();
          src.buffer = decoded;
          src.connect(ctx.destination);
          src.onended = () => resolve();
          src.start();
        });
      } catch { /* swallow decode/playback errors */ }
    });
    this.queue = job;
    return job;
  }

  stop(): void {
    try { this.ctx?.close(); } catch { /* ignore */ }
    this.ctx = null;
    this.queue = Promise.resolve();
  }
}
